<script setup>
import { computed } from 'vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'

const props = defineProps({
  show: { type: Boolean, default: false },
  title: { type: String, default: '' },
  size: {
    type: String,
    default: 'md',
    validator: v =>
      ['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl'].includes(
        v
      ),
  },
  role: { type: String, default: 'dialog' },
  modalStyle: { type: String, default: 'window' },
  useFocusTrap: { type: Boolean, default: true },
  stackPriority: {
    type: String,
    default: 'default',
    validator: v => ['default', 'top'].includes(v),
  },
})

const emit = defineEmits(['close', 'close-via-escape', 'showing', 'closing'])

const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')

const useBottomSheet = computed(
  () => props.modalStyle === 'window' && isMobile.value
)

function handleSheetUpdate(value) {
  if (!value) {
    emit('close')
  }
}
</script>

<template>
  <BottomSheet
    v-if="useBottomSheet"
    :model-value="show"
    :title="title"
    :snap="snap"
    :stack-priority="stackPriority"
    @update:model-value="handleSheetUpdate"
  >
    <slot name="sheet-content">
      <slot />
    </slot>

    <template v-if="$slots.footer" #footer>
      <slot name="footer" />
    </template>
  </BottomSheet>

  <Modal
    v-else
    :show="show"
    :size="size"
    :role="role"
    :modal-style="modalStyle"
    :use-focus-trap="useFocusTrap"
    :stack-priority="stackPriority"
    @close-via-escape="$emit('close-via-escape', $event)"
    @showing="$emit('showing')"
    @closing="$emit('closing')"
  >
    <slot />
  </Modal>
</template>
